import { lazySelector } from '@sg-core/resources/assets/js/utils';

const SCROLL_EDGE_THRESHOLD = 1; // in pixels
// Once an edge is reached, the scroll position must move at least this far away
// before we re-enable its mask. Writing the mask can nudge the measured scroll
// metrics by a few px on WebKit; without this deadband that wobble flips the
// mask on/off every frame, relayouts the container and makes it shake.
const EDGE_HYSTERESIS = 8; // in pixels
const MASK_START_OPACITY_VAR = '--_comp-scroll-container-mask-start-opacity';
const MASK_END_OPACITY_VAR = '--_comp-scroll-container-mask-end-opacity';

export class ScrollContainer {
  private readonly el: HTMLElement;
  private readonly resizeObserver: ResizeObserver | null;
  private rafId: number | null = null;
  private atStart = true;
  private atEnd = true;

  constructor(el: string | HTMLElement) {
    this.el = lazySelector(el);
    this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());

    this.el.addEventListener('scroll', this.handleScroll, { passive: true });
    this.resizeObserver?.observe(this.el);
    this.updateState();
  }

  private readonly handleScroll = () => {
    this.scheduleUpdate();
  };

  // Coalesce updates into the next frame so we never write the mask custom
  // properties synchronously from inside the ResizeObserver callback (that
  // retriggers the observer on WebKit with a "ResizeObserver loop" error).
  private scheduleUpdate() {
    if (this.rafId !== null) {
      return;
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updateState();
    });
  }

  private updateState() {
    const maxScrollLeft = Math.max(this.el.scrollWidth - this.el.clientWidth, 0);
    const isScrollable = maxScrollLeft > SCROLL_EDGE_THRESHOLD;
    const scrollLeft = Math.abs(this.el.scrollLeft);

    // Hysteresis: leaving an edge requires moving EDGE_HYSTERESIS px away from it,
    // reaching it only SCROLL_EDGE_THRESHOLD px — so a few px of layout wobble at
    // the boundary can't toggle the masks.
    const startLeaveThreshold = this.atStart ? EDGE_HYSTERESIS : SCROLL_EDGE_THRESHOLD;
    const endLeaveThreshold = this.atEnd ? EDGE_HYSTERESIS : SCROLL_EDGE_THRESHOLD;

    this.atStart = scrollLeft <= startLeaveThreshold;
    this.atEnd = scrollLeft >= maxScrollLeft - endLeaveThreshold;

    const canScrollStart = isScrollable && !this.atStart;
    const canScrollEnd = isScrollable && !this.atEnd;

    this.setMaskOpacity(MASK_START_OPACITY_VAR, canScrollStart ? '0' : '1');
    this.setMaskOpacity(MASK_END_OPACITY_VAR, canScrollEnd ? '0' : '1');
  }

  // Only write when the value actually changes: re-setting the (transitioned)
  // custom property keeps feeding the WebKit relayout loop described above.
  private setMaskOpacity(name: string, value: string) {
    if (this.el.style.getPropertyValue(name) !== value) {
      this.el.style.setProperty(name, value);
    }
  }

  public destroy() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.resizeObserver?.disconnect();
    this.el.removeEventListener('scroll', this.handleScroll);

    this.el.style.removeProperty(MASK_START_OPACITY_VAR);
    this.el.style.removeProperty(MASK_END_OPACITY_VAR);
  }
}
